<template>
  <v-row>
    <!-- FILE NAME -->
    <v-col :cols="8" :data-test-id="`attached-${fileName}`">
      <button
        v-if="
          attachmentType === 'attachment' &&
          currentDocument?.documentStatus !== DocumentStatus.DRAFT
        "
        class="w-100 text-left text-black text-truncate"
        :class="{
          'text-decoration-underline':
            attachmentType === 'attachment' &&
            currentDocument?.documentStatus !== DocumentStatus.DRAFT
        }"
        @click="emit('preview-file', file)"
      >
        {{ fileName }}
      </button>
      <span v-else class="w-100 d-block text-black text-truncate">
        {{ fileName }}
      </span>
    </v-col>
    <!-- /FILE NAME -->

    <!-- Status -->
    <v-col
      :cols="
        file.status === AttachmentStatus.PENDING ||
        file.status === AttachmentStatus.PROGRESS ||
        file.status === AttachmentStatus.DISABLED
          ? 4
          : 3
      "
    >
      <!-- Error state -->
      <div
        v-if="
          file.status === AttachmentStatus.EXCLUDED ||
          file.status === AttachmentStatus.ERROR
        "
        class="d-flex flex-column align-end"
        data-test-id="exclamation-triangle"
      >
        <v-menu location="left top" max-width="300" open-on-click open-on-hover>
          <template #activator="{ props: ActivatorProps }">
            <v-icon
              v-bind="ActivatorProps"
              color="warning"
              data-test-id="icon-password-protected"
              size="16"
            >
              fal fa-triangle-exclamation
            </v-icon>
          </template>
          <v-card>
            <v-card-text data-test-id="label-tooltip-password-protected">
              {{
                file.errorId
                  ? i18n.t(`errors.api.${file.errorId}`)
                  : i18n.t('texts.file_too_large')
              }}
            </v-card-text>
          </v-card>
        </v-menu>
      </div>

      <!-- Attached state -->
      <div
        v-if="file.status === AttachmentStatus.ATTACHED"
        class="d-flex justify-end text-black text-body-medium"
        data-test-id="attachment-size"
      >
        <p>{{ fileSize }}</p>
      </div>

      <!-- Inprogress state -->
      <div
        v-if="
          file.status === AttachmentStatus.PROGRESS ||
          file.status === AttachmentStatus.DISABLED
        "
        class="d-flex flex-column align-end"
      >
        <v-icon class="fa-spin" color="grey" size="16">
          fal fa-spinner-third
        </v-icon>
      </div>
      <div
        v-if="file.status === AttachmentStatus.PENDING"
        class="d-flex flex-column align-end"
      >
        <v-icon size="16"> fal fa-hourglass-start </v-icon>
      </div>

      <!-- Unsupported state -->
      <div
        v-if="file.status === AttachmentStatus.UNSUPPORTED"
        class="d-flex flex-column align-end"
      >
        <v-menu location="left top" max-width="300" open-on-click open-on-hover>
          <template #activator="{ props: ActivatorProps }">
            <v-icon v-bind="ActivatorProps" color="error" size="14">
              far fa-file-slash
            </v-icon>
          </template>
          <v-card>
            <v-card-text class="signature-level-description">
              {{ i18n.t('texts.unsupported_attachment') }}
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </v-col>

    <!-- Actions -->
    <v-col
      v-if="
        file.status !== AttachmentStatus.PENDING &&
        file.status !== AttachmentStatus.PROGRESS &&
        file.status !== AttachmentStatus.DISABLED
      "
      :cols="1"
    >
      <div class="h-100 d-flex align-center justify-center">
        <!-- REMOVE -->
        <!-- //currentDocument?.documentStatus === DocumentStatus.DRAFT -->
        <template
          v-if="
            currentDocument?.documentStatus === DocumentStatus.DRAFT &&
            attachmentType === 'attachment'
          "
        >
          <v-icon
            @click="emit('remove-uploaded-attachment', props.file)"
            @keydown.enter="emit('remove-uploaded-attachment', props.file)"
            @keydown.space="emit('remove-uploaded-attachment', props.file)"
          >
            fal fa-close
          </v-icon>
        </template>
        <template
          v-if="
            currentDocument?.documentStatus !== DocumentStatus.DRAFT &&
            attachmentType === 'signee-attachment'
          "
        >
          <v-icon
            data-test-id="remove-file"
            @click="emit('remove-uploaded-attachment', props.file)"
            @keydown.enter="emit('remove-uploaded-attachment', props.file)"
            @keydown.space="emit('remove-uploaded-attachment', props.file)"
          >
            fal fa-close
          </v-icon>
        </template>
        <!-- /REMOVE -->

        <template
          v-if="
            currentDocument?.documentStatus !== DocumentStatus.DRAFT &&
            attachmentType === 'attachment'
          "
        >
          <!-- DOWNLOAD -->
          <!-- currentDocument?.documentStatus !== DocumentStatus.DRAFT -->

          <v-icon
            v-tooltip="i18n.t('buttons.download')"
            :aria-label="i18n.t('buttons.download')"
            color="black"
            :data-test-id="`download-attachment-${fileName}`"
            size="16"
            @click="emit('download-file', props.file)"
          >
            far fa-download
          </v-icon>
          <!-- /DOWNLOAD -->
        </template>
      </div>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { AttachmentStatus } from '@/types/enums/AttachmentStatus.ts';
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { humanFileSize } from '@/composables/useUtils';
import type { AttachmentFile } from '@/types/Attachment';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { DocumentStatus } from '@/types/enums/DocumentStatus';
import type { AttachedFile } from '@/types/FileType';

const props = defineProps({
  file: {
    type: Object as PropType<AttachmentFile> as PropType<AttachedFile>,
    required: true
  },
  excluded: {
    type: Boolean,
    default: false
  },
  attachmentType: {
    type: String as PropType<'attachment' | 'signee-attachment'>,
    default: undefined
  }
});

const emit = defineEmits([
  'remove-uploaded-attachment',
  'download-file',
  'preview-file'
]);
const { currentDocument } = useDocumentContext();
const i18n = useI18n();

const fileSize = computed(() => {
  return props.file?.status === AttachmentStatus.ATTACHED &&
    props.file &&
    props.file.size
    ? humanFileSize(props.file.size)
    : humanFileSize(props.file.attachment.size);
});

const fileName = computed(() => {
  console.log(props.file);
  return props.file?.name || props.file.attachment.name;
});
</script>

<style scoped lang="scss">
.tooltip {
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2) !important;
}
</style>
